import { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminUser, AdminRole } from "../../../types/admin";

const PAGE_SIZE = 20;

export function useUsersAdmin() {
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    // page_size no máximo permitido pro back — este picker de papéis (form de
    // usuário) precisa de TODOS os papéis, não de uma página.
    adminApi
      .listRoles({ page_size: 200 })
      .then((data) => setRoles(data.roles))
      .catch(() =>
        toast({ title: "Erro ao carregar papéis", status: "error", duration: 4000, isClosable: true }),
      );
  }, [toast]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const data = await adminApi.listUsers({
        search, is_active: isActiveFilter, exclude_candidates: true, page, page_size: PAGE_SIZE,
      });
      setUsers(data.users);
      setTotalPages(data.total_pages);
    } catch {
      setError(true);
      toast({ title: "Erro ao carregar usuários", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [search, isActiveFilter, page, toast]);

  useEffect(() => { setPage(1); }, [search, isActiveFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleActive = async (id: number) => {
    try {
      await adminApi.toggleActive(id);
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, is_active: !u.is_active } : u)));
    } catch {
      toast({ title: "Erro ao alterar status do usuário", status: "error", duration: 4000, isClosable: true });
    }
  };

  const handleEditOpen = (user: AdminUser) => {
    setSelectedUser(user);
    setIsEditOpen(true);
  };

  const handleLogsOpen = (user: AdminUser) => {
    setSelectedUser(user);
    setIsLogsOpen(true);
  };

  return {
    users,
    roles,
    loading,
    error,
    search,
    setSearch,
    isActiveFilter,
    setIsActiveFilter,
    page,
    totalPages,
    setPage,
    isEditOpen,
    setIsEditOpen,
    isLogsOpen,
    setIsLogsOpen,
    isCreateOpen,
    setIsCreateOpen,
    selectedUser,
    handleToggleActive,
    handleEditOpen,
    handleLogsOpen,
    refreshList: fetchUsers,
  };
}
